import React, { useState, useMemo } from "react";
import { PiggyBank, Plus, Pencil, Trash2, Info, TrendingUp, CheckCircle, Lightbulb, AlertTriangle } from "lucide-react";
import PensionContributionModal from "./PensionContributionModal";
import { DEDUCTIBILITY_LIMIT } from "../../hooks/useAppState";

// Tipi di contributi per la visualizzazione
const CONTRIBUTION_TYPE_LABELS = {
  INPS_VOLONTARIO: "INPS volontario",
  FONDO_PENSIONE: "Fondo pensione",
  ALTRO: "Altro",
};

function formatCurrency(value) {
  const num = Number(value || 0);
  const abs = Math.abs(num);
  const sign = num < 0 ? "-" : "";
  const [intPart, decPart] = abs.toFixed(2).split(".");
  const groupedInt = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  return `${sign}${groupedInt},${decPart} €`;
}

function formatDate(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  return d.toLocaleDateString("it-IT", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

export default function PensionPlanCard({
  selectedYear,
  pensionPlan,
  updatePensionGoal, // Kept for backwards compatibility, not used
  addPensionContribution,
  updatePensionContribution,
  deletePensionContribution,
  profitabilityCoefficient,
  taxRate,
  darkMode = false,
}) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingContribution, setEditingContribution] = useState(null);

  // Filtra contributi per anno selezionato
  const contributionsForYear = useMemo(() => {
    return (pensionPlan.contributions || [])
      .filter((c) => {
        if (!c.date) return false;
        const year = new Date(c.date).getFullYear();
        return year === selectedYear;
      })
      .sort((a, b) => new Date(b.date) - new Date(a.date)); // Più recenti prima
  }, [pensionPlan.contributions, selectedYear]);

  // Totale versato nell'anno
  const totalContributions = useMemo(() => {
    return contributionsForYear.reduce((sum, c) => sum + (c.amount || 0), 0);
  }, [contributionsForYear]);

  // Calcoli soglia deducibilità
  const remainingDeductible = Math.max(0, DEDUCTIBILITY_LIMIT - totalContributions);
  const percentageUsed = (totalContributions / DEDUCTIBILITY_LIMIT) * 100;
  const exceededAmount = Math.max(0, totalContributions - DEDUCTIBILITY_LIMIT);

  // Calcolo dinamico suggerimento mensile basato sui mesi rimanenti
  const dynamicSuggestion = useMemo(() => {
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth() + 1; // 1-12

    // Se stiamo guardando un anno passato, non mostrare suggerimenti
    if (selectedYear < currentYear) {
      return { type: "past", message: null };
    }

    // Se stiamo guardando un anno futuro
    if (selectedYear > currentYear) {
      const monthlyAmount = Math.ceil(DEDUCTIBILITY_LIMIT / 12);
      return {
        type: "future",
        message: `Versa ${monthlyAmount}€/mese per raggiungere il limite di ${formatCurrency(DEDUCTIBILITY_LIMIT)}`,
        monthlyAmount,
        monthsRemaining: 12,
      };
    }

    // Anno corrente: calcolo dinamico
    const monthsRemaining = 12 - currentMonth + 1; // es. Febbraio: 12 - 2 + 1 = 11
    const amountRemaining = DEDUCTIBILITY_LIMIT - totalContributions;

    // Già raggiunto il limite
    if (amountRemaining <= 0) {
      if (exceededAmount > 0) {
        return {
          type: "exceeded",
          message: `Hai superato il limite. ${formatCurrency(exceededAmount)} non saranno deducibili.`,
          exceededAmount,
        };
      }
      return {
        type: "reached",
        message: `Hai raggiunto il limite di deducibilità per il ${selectedYear}!`,
      };
    }

    // Calcola suggerimento mensile
    const monthlyAmount = Math.ceil(amountRemaining / monthsRemaining);

    // Caso speciale: Dicembre
    if (currentMonth === 12) {
      return {
        type: "december",
        message: `Hai tempo fino al 31/12 per versare altri ${formatCurrency(amountRemaining)}`,
        amountRemaining,
      };
    }

    return {
      type: "suggestion",
      message: `Versa ${monthlyAmount}€/mese per i prossimi ${monthsRemaining} mesi`,
      subMessage: `per raggiungere il limite di deducibilità`,
      monthlyAmount,
      monthsRemaining,
      amountRemaining,
    };
  }, [selectedYear, totalContributions, exceededAmount]);

  // Calcolo risparmio fiscale (per forfettari)
  // I contributi versati riducono l'imponibile dell'anno successivo
  const taxSaving = useMemo(() => {
    const deductibleAmount = Math.min(totalContributions, DEDUCTIBILITY_LIMIT);
    // Risparmio = contributi × coeff. redditività × aliquota
    // Questo perché riducono l'imponibile lordo del prossimo anno
    const savingOnTax = deductibleAmount * profitabilityCoefficient * (taxRate / 100);
    return savingOnTax;
  }, [totalContributions, profitabilityCoefficient, taxRate]);

  // Stile barra progressione
  const getProgressBarStyle = () => {
    if (percentageUsed >= 100) {
      return darkMode
        ? "bg-gradient-to-r from-violet-600 to-purple-500"
        : "bg-gradient-to-r from-violet-500 to-purple-400";
    }
    if (percentageUsed >= 80) {
      return darkMode
        ? "bg-gradient-to-r from-amber-600 to-orange-500"
        : "bg-gradient-to-r from-amber-500 to-orange-400";
    }
    return darkMode
      ? "bg-gradient-to-r from-emerald-600 to-teal-500"
      : "bg-gradient-to-r from-emerald-500 to-teal-400";
  };

  const handleOpenModal = (contribution = null) => {
    setEditingContribution(contribution);
    setIsModalOpen(true);
  };

  const handleSaveContribution = (data) => {
    if (data.id) {
      updatePensionContribution(data.id, data);
    } else {
      addPensionContribution(data);
    }
  };

  const handleDeleteContribution = (id) => {
    if (window.confirm("Eliminare questo versamento?")) {
      deletePensionContribution(id);
    }
  };

  return (
    <>
      <section
        className={`rounded-2xl border-2 p-6 ${
          darkMode
            ? "border-forfy-700 bg-indigo-900/20"
            : "border-forfy-100 bg-forfy-50/50"
        }`}
      >
        <div className="flex items-center gap-2 mb-4">
          <PiggyBank
            className={`w-5 h-5 ${
              darkMode ? "text-forfy-300" : "text-forfy-600"
            }`}
          />
          <h2
            className={`text-sm font-semibold uppercase tracking-wide ${
              darkMode ? "text-forfy-300" : "text-indigo-900"
            }`}
          >
            Piano Pensionistico
          </h2>
        </div>

        <p className={`text-sm mb-6 ${darkMode ? "text-neutral-500" : "text-neutral-600"}`}>
          Contributi volontari per la tua pensione futura
        </p>

        <div className="space-y-6">

          {/* SEZIONE 2: Soglia deducibilità */}
          <div
            className={`p-4 rounded-xl ${
              darkMode ? "bg-neutral-900" : "bg-white"
            } border ${darkMode ? "border-neutral-800" : "border-neutral-200"}`}
          >
            <div className="flex items-center justify-between mb-3">
              <h3
                className={`text-xs font-semibold uppercase tracking-wide ${
                  darkMode ? "text-neutral-500" : "text-neutral-500"
                }`}
              >
                Soglia Deducibilità {formatCurrency(DEDUCTIBILITY_LIMIT)}
              </h3>
              <span
                className={`text-sm font-medium ${
                  percentageUsed >= 100
                    ? darkMode
                      ? "text-violet-400"
                      : "text-violet-600"
                    : darkMode
                    ? "text-neutral-400"
                    : "text-neutral-700"
                }`}
              >
                {Math.min(100, Math.round(percentageUsed))}%
              </span>
            </div>

            {/* Barra di progressione */}
            <div
              className={`h-4 rounded-full overflow-hidden ${
                darkMode ? "bg-neutral-800" : "bg-neutral-200"
              }`}
            >
              <div
                className={`h-full rounded-full transition-all ${getProgressBarStyle()}`}
                style={{ width: `${Math.min(100, percentageUsed)}%` }}
              />
            </div>

            <div className="mt-3 flex flex-wrap justify-between gap-2">
              <div>
                <span className={`text-sm ${darkMode ? "text-neutral-500" : "text-neutral-600"}`}>
                  Versato:{" "}
                </span>
                <span
                  className={`font-semibold ${
                    darkMode ? "text-white" : "text-neutral-900"
                  }`}
                >
                  {formatCurrency(totalContributions)}
                </span>
              </div>
              <div>
                <span className={`text-sm ${darkMode ? "text-neutral-500" : "text-neutral-600"}`}>
                  Rimanente:{" "}
                </span>
                <span
                  className={`font-semibold ${
                    darkMode ? "text-emerald-400" : "text-emerald-600"
                  }`}
                >
                  {formatCurrency(remainingDeductible)}
                </span>
              </div>
            </div>

            {/* Suggerimento dinamico basato sui mesi rimanenti */}
            {dynamicSuggestion.type === "suggestion" && (
              <div
                className={`mt-4 flex items-start gap-2 p-3 rounded-lg ${
                  darkMode ? "bg-forfy-900/30" : "bg-forfy-50/50"
                }`}
              >
                <Lightbulb
                  className={`w-4 h-4 mt-0.5 shrink-0 ${
                    darkMode ? "text-forfy-300" : "text-forfy-600"
                  }`}
                />
                <div>
                  <p
                    className={`text-sm font-medium ${
                      darkMode ? "text-forfy-300" : "text-forfy-700"
                    }`}
                  >
                    {dynamicSuggestion.message}
                  </p>
                  {dynamicSuggestion.subMessage && (
                    <p
                      className={`text-xs mt-1 ${
                        darkMode ? "text-forfy-300" : "text-forfy-600"
                      }`}
                    >
                      {dynamicSuggestion.subMessage}
                    </p>
                  )}
                </div>
              </div>
            )}

            {dynamicSuggestion.type === "future" && (
              <div
                className={`mt-4 flex items-start gap-2 p-3 rounded-lg ${
                  darkMode ? "bg-forfy-900/30" : "bg-forfy-50/50"
                }`}
              >
                <Lightbulb
                  className={`w-4 h-4 mt-0.5 shrink-0 ${
                    darkMode ? "text-forfy-300" : "text-forfy-600"
                  }`}
                />
                <p
                  className={`text-sm ${
                    darkMode ? "text-forfy-300" : "text-forfy-700"
                  }`}
                >
                  {dynamicSuggestion.message}
                </p>
              </div>
            )}

            {dynamicSuggestion.type === "december" && (
              <div
                className={`mt-4 flex items-start gap-2 p-3 rounded-lg ${
                  darkMode ? "bg-amber-900/30" : "bg-amber-100"
                }`}
              >
                <AlertTriangle
                  className={`w-4 h-4 mt-0.5 shrink-0 ${
                    darkMode ? "text-amber-400" : "text-amber-600"
                  }`}
                />
                <p
                  className={`text-sm ${
                    darkMode ? "text-amber-300" : "text-amber-700"
                  }`}
                >
                  {dynamicSuggestion.message}
                </p>
              </div>
            )}

            {dynamicSuggestion.type === "reached" && (
              <div
                className={`mt-4 flex items-start gap-2 p-3 rounded-lg ${
                  darkMode ? "bg-violet-900/30" : "bg-violet-100"
                }`}
              >
                <CheckCircle
                  className={`w-4 h-4 mt-0.5 shrink-0 ${
                    darkMode ? "text-violet-400" : "text-violet-600"
                  }`}
                />
                <p
                  className={`text-sm ${
                    darkMode ? "text-violet-300" : "text-violet-700"
                  }`}
                >
                  {dynamicSuggestion.message}
                </p>
              </div>
            )}

            {dynamicSuggestion.type === "exceeded" && (
              <div
                className={`mt-4 flex items-start gap-2 p-3 rounded-lg ${
                  darkMode ? "bg-amber-900/30" : "bg-amber-100"
                }`}
              >
                <AlertTriangle
                  className={`w-4 h-4 mt-0.5 shrink-0 ${
                    darkMode ? "text-amber-400" : "text-amber-600"
                  }`}
                />
                <p
                  className={`text-sm ${
                    darkMode ? "text-amber-300" : "text-amber-700"
                  }`}
                >
                  {dynamicSuggestion.message}
                </p>
              </div>
            )}
          </div>

          {/* SEZIONE 3: Lista versamenti */}
          <div
            className={`p-4 rounded-xl ${
              darkMode ? "bg-neutral-900" : "bg-white"
            } border ${darkMode ? "border-neutral-800" : "border-neutral-200"}`}
          >
            <div className="flex items-center justify-between mb-3">
              <h3
                className={`text-xs font-semibold uppercase tracking-wide ${
                  darkMode ? "text-neutral-500" : "text-neutral-500"
                }`}
              >
                Versamenti {selectedYear}
              </h3>
              <button
                onClick={() => handleOpenModal(null)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  darkMode
                    ? "bg-forfy-500 text-white hover:bg-forfy-500"
                    : "bg-forfy-500 text-white hover:bg-forfy-600"
                }`}
              >
                <Plus className="w-4 h-4" />
                Aggiungi
              </button>
            </div>

            {contributionsForYear.length === 0 ? (
              <p className={`text-sm ${darkMode ? "text-neutral-600" : "text-neutral-500"}`}>
                Nessun versamento registrato per il {selectedYear}
              </p>
            ) : (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {contributionsForYear.map((c) => (
                  <div
                    key={c.id}
                    className={`flex items-center justify-between py-2 border-b last:border-0 ${
                      darkMode ? "border-neutral-800" : "border-neutral-100"
                    }`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span
                          className={`text-sm ${
                            darkMode ? "text-neutral-500" : "text-neutral-500"
                          }`}
                        >
                          {formatDate(c.date)}
                        </span>
                        <span
                          className={`text-sm ${
                            darkMode ? "text-neutral-400" : "text-neutral-700"
                          }`}
                        >
                          {CONTRIBUTION_TYPE_LABELS[c.type] || c.type}
                        </span>
                      </div>
                      {c.notes && (
                        <p
                          className={`text-xs truncate ${
                            darkMode ? "text-neutral-600" : "text-neutral-400"
                          }`}
                        >
                          {c.notes}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-2 ml-3">
                      <span
                        className={`font-semibold ${
                          darkMode ? "text-white" : "text-neutral-900"
                        }`}
                      >
                        {formatCurrency(c.amount)}
                      </span>
                      <button
                        onClick={() => handleOpenModal(c)}
                        className={`p-1.5 rounded-lg transition-colors ${
                          darkMode
                            ? "hover:bg-neutral-800 text-neutral-500"
                            : "hover:bg-neutral-100 text-neutral-500"
                        }`}
                        aria-label="Modifica versamento"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteContribution(c.id)}
                        className={`p-1.5 rounded-lg transition-colors ${
                          darkMode
                            ? "hover:bg-red-900/50 text-red-400"
                            : "hover:bg-red-50 text-red-500"
                        }`}
                        aria-label="Elimina versamento"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}

                {/* Totale */}
                <div
                  className={`flex items-center justify-between pt-3 border-t ${
                    darkMode ? "border-neutral-700" : "border-neutral-200"
                  }`}
                >
                  <span
                    className={`text-sm font-medium ${
                      darkMode ? "text-neutral-400" : "text-neutral-700"
                    }`}
                  >
                    Totale versato {selectedYear}:
                  </span>
                  <span
                    className={`font-bold text-lg ${
                      darkMode ? "text-forfy-300" : "text-forfy-600"
                    }`}
                  >
                    {formatCurrency(totalContributions)}
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* SEZIONE 4: Risparmio fiscale stimato */}
          {totalContributions > 0 && (
            <div
              className={`p-4 rounded-xl ${
                darkMode ? "bg-neutral-900" : "bg-white"
              } border ${darkMode ? "border-neutral-800" : "border-neutral-200"}`}
            >
              <div className="flex items-center gap-2 mb-3">
                <TrendingUp
                  className={`w-4 h-4 ${
                    darkMode ? "text-emerald-400" : "text-emerald-600"
                  }`}
                />
                <h3
                  className={`text-xs font-semibold uppercase tracking-wide ${
                    darkMode ? "text-neutral-500" : "text-neutral-500"
                  }`}
                >
                  Risparmio Fiscale Stimato
                </h3>
              </div>

              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className={darkMode ? "text-neutral-500" : "text-neutral-600"}>
                    Contributi versati:
                  </span>
                  <span className={darkMode ? "text-white" : "text-neutral-900"}>
                    {formatCurrency(Math.min(totalContributions, DEDUCTIBILITY_LIMIT))}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className={darkMode ? "text-neutral-500" : "text-neutral-600"}>
                    × Coeff. redditività:
                  </span>
                  <span className={darkMode ? "text-white" : "text-neutral-900"}>
                    {Math.round(profitabilityCoefficient * 100)}%
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className={darkMode ? "text-neutral-500" : "text-neutral-600"}>
                    × Aliquota imposta:
                  </span>
                  <span className={darkMode ? "text-white" : "text-neutral-900"}>
                    {taxRate}%
                  </span>
                </div>
                <div
                  className={`border-t pt-2 mt-2 ${
                    darkMode ? "border-neutral-800" : "border-neutral-200"
                  }`}
                >
                  <div className="flex justify-between">
                    <span
                      className={`font-medium ${
                        darkMode ? "text-emerald-400" : "text-emerald-700"
                      }`}
                    >
                      Risparmio su imponibile {selectedYear + 1}:
                    </span>
                    <span
                      className={`font-bold ${
                        darkMode ? "text-emerald-400" : "text-emerald-600"
                      }`}
                    >
                      ~{formatCurrency(taxSaving)}
                    </span>
                  </div>
                </div>
              </div>

              <div
                className={`mt-4 flex items-start gap-2 p-3 rounded-lg ${
                  darkMode ? "bg-neutral-800/50" : "bg-neutral-50"
                }`}
              >
                <Info
                  className={`w-4 h-4 mt-0.5 shrink-0 ${
                    darkMode ? "text-neutral-500" : "text-neutral-500"
                  }`}
                />
                <p
                  className={`text-xs ${
                    darkMode ? "text-neutral-500" : "text-neutral-600"
                  }`}
                >
                  I contributi versati nel {selectedYear} riducono l'imponibile del{" "}
                  {selectedYear + 1} (principio di cassa)
                </p>
              </div>
            </div>
          )}

          {/* Disclaimer */}
          <div
            className={`flex items-start gap-2 p-3 rounded-lg ${
              darkMode ? "bg-forfy-900/30" : "bg-forfy-50/50"
            }`}
          >
            <Info
              className={`w-4 h-4 mt-0.5 shrink-0 ${
                darkMode ? "text-forfy-300" : "text-forfy-600"
              }`}
            />
            <p
              className={`text-xs ${
                darkMode ? "text-forfy-300" : "text-forfy-700"
              }`}
            >
              I forfettari con solo reddito da P.IVA non possono dedurre direttamente i
              contributi previdenziali dall'imposta sostitutiva. Tuttavia, i contributi
              INPS versati riducono l'imponibile dell'anno successivo.
            </p>
          </div>
        </div>
      </section>

      {/* Modal per aggiungere/modificare versamento */}
      <PensionContributionModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setEditingContribution(null);
        }}
        onSave={handleSaveContribution}
        contribution={editingContribution}
        darkMode={darkMode}
      />
    </>
  );
}
